/**
 * 「方案与定价」页内容。
 *
 * 价格一律是「咨询报价」：平台按机体数量、装在哪、要改多少来算，
 * 页面上不写臆造的数字，只写清楚三档分别包含什么、边界在哪。
 */

export interface TierAction {
  label: string;
  to: string;
  external?: boolean;
  variant: 'primary' | 'secondary' | 'ghost';
}

export interface Tier {
  id: string;
  name: string;
  tagline: string;
  price: string;
  priceNote: string;
  audience: string;
  features: readonly string[];
  excluded?: readonly string[];
  action: TierAction;
  highlighted?: boolean;
}

export const PRICING_INTRO = {
  eyebrow: '方案与定价',
  title: '三档方案，依机体规模与部署方式选择',
  lead: '基础版用于打通链路，专业版支撑日常运营，私有化定制则将整套平台交由贵方掌控。价格依实际场景与规模报价，页面上不呈现未经核实的数字。',
} as const;

export const TIERS: readonly Tier[] = [
  {
    id: 'basic',
    name: '基础版',
    tagline: '先打通完整链路',
    price: '咨询报价',
    priceNote: '按机体数量计价',
    audience: '个人开发者、教学与实验、单机或小规模自用',
    features: [
      '机队总览、无人机管理、单机管理',
      '实时遥测：WebSocket 推流，中断时降级为 REST 轮询',
      '内置模拟机体，可注入断链与慢响应',
      '四类内置角色与权限定义',
      '三级告警阈值，支持单机覆盖',
    ],
    excluded: ['审计日志长期保留', '私有化部署'],
    action: { label: '申请试用', to: '#trial', variant: 'primary' },
  },
  {
    id: 'pro',
    name: '专业版',
    tagline: '支撑日常运营',
    price: '咨询报价',
    priceNote: '按机体数量与值守规模计价',
    audience: '巡检作业队、园区与起降场运营方',
    features: [
      '包含基础版的全部能力',
      '审计日志与操作留档，默认保留 180 天',
      '平台级与单机级的阈值分层管理',
      '设备档案批量导入与凭据集中轮换',
      '值班观察员席位与全屏值守流程',
      '接入排障支持与联调协助',
    ],
    action: { label: '申请试用', to: '#trial', variant: 'primary' },
    highlighted: true,
  },
  {
    id: 'private',
    name: '私有化定制',
    tagline: '部署至贵方自有账号',
    price: '咨询报价',
    priceNote: '按部署方式与定制范围报价',
    audience: '运营企业、集成商、对数据归属要求较高的机构',
    features: [
      '包含专业版的全部能力',
      '部署至贵方 Cloudflare 账号与域名',
      '账号体系与数据完全由贵方掌握',
      '界面命名、角色划分与流程按需定制',
      '与既有业务系统的接口对接',
      '配套软硬件的方案沟通',
    ],
    action: { label: '沟通需求', to: '/contact', variant: 'secondary' },
  },
];

export const PRICING_NOTE =
  '我们不销售无人机整机。方案包含平台软件、部署与定制服务，以及必要时的配套软硬件方案沟通；机体的采购与选型完全由贵方决定。';

export interface CompareRow {
  feature: string;
  basic: string;
  pro: string;
  private: string;
}

export const COMPARE_TABLE = {
  head: ['能力', '基础版', '专业版', '私有化定制'],
  rows: [
    ['机队总览与实时遥测', 'yes', 'yes', 'yes'],
    ['单机管理与指令下发', 'yes', 'yes', 'yes'],
    ['内置模拟机体', 'yes', 'yes', 'yes'],
    ['角色与权限定义', 'yes', 'yes', 'yes'],
    ['三级告警阈值与单机覆盖', 'yes', 'yes', 'yes'],
    ['审计日志', '短期', '180 天', '按需配置'],
    ['阈值分层管理', 'no', 'yes', 'yes'],
    ['批量导入与凭据轮换', 'no', 'yes', 'yes'],
    ['排障与联调支持', '社区', '包含', '包含'],
    ['私有化部署', 'no', 'no', 'yes'],
    ['界面与流程定制', 'no', 'no', 'yes'],
    ['业务系统对接', 'no', 'no', 'yes'],
  ],
} as const;

export interface BillingPoint {
  title: string;
  text: string;
}

export const BILLING_POINTS: readonly BillingPoint[] = [
  {
    title: '按机体数量',
    text: '平台价值随纳管机体数量增长，因此这始终是计价的主要维度，不按用户席位逐层加价。',
  },
  {
    title: '按部署方式',
    text: '托管于 Havorix™ 侧与部署至贵方自有账号，运维责任与成本结构截然不同，故分开计价。',
  },
  {
    title: '按定制范围',
    text: '界面定制、流程改造与系统对接按实际工作量单独评估，评估结论将写入方案文档。',
  },
];

export const TRIAL_COPY = {
  eyebrow: '申请试用',
  title: '一封邮件即可开始',
  text: '本页面不收集也不上传任何数据：填写完成后点击「生成邮件」，浏览器仅在本地拼装一封邮件并交由您的邮箱客户端。若不便使用邮箱客户端，也可一键复制正文，发送至下方地址。',
  submitLabel: '生成邮件',
  copyLabel: '复制邮件正文',
  copiedLabel: '已复制',
  resetLabel: '清空表单',
  privacyNote: '表单内容仅存在于当前页面，刷新即消失。本站不会保存、不会转发，也不会用于分析。',
} as const;

/** 邮件主题前缀，实际主题由 `buildMailSubject` 在此基础上拼接。 */
export const TRIAL_MAIL_SUBJECT_PREFIX = '[试用申请] Havorix™';
export const TRIAL_MAIL_HEADING = 'Havorix™ 试用 / 合作申请';
